/**
 * Tenant context store for the SMS Marketing standalone SaaS app.
 *
 * Fetches org info from the backend on app load and exposes it
 * via a Zustand store for use across the app.
 *
 * @module lib/tenant
 */

import { createAppStore } from "@plerity/dashboard-shared/stores";
import { client } from "../api/client";

export interface TenantState {
  org_id: number | null;
  org_name: string | null;
  hostname: string | null;
  plan_tier: string;
  features: string[];
  loading: boolean;
  error: string | null;
}

interface TenantActions {
  fetchTenantContext: () => Promise<void>;
  reset: () => void;
}

const initialState: TenantState = {
  org_id: null,
  org_name: null,
  hostname: null,
  plan_tier: "sms_basic",
  features: [],
  loading: false,
  error: null,
};

export const useTenantStore = createAppStore<TenantState & TenantActions>("sms-tenant", (set) => ({
  ...initialState,

  fetchTenantContext: async () => {
    set({ loading: true, error: null });
    try {
      const response = await client.get<{
        success: boolean;
        data: {
          org_id: number;
          org_name: string;
          hostname: string | null;
          plan_tier: string;
          features: string[];
        };
      }>("/api/sms/tenant-context");

      const { data } = response.data;
      set({
        org_id: data.org_id,
        org_name: data.org_name,
        hostname: data.hostname,
        plan_tier: data.plan_tier,
        features: data.features,
        loading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load tenant context";
      set({ error: message, loading: false });
    }
  },

  reset: () => set(initialState),
}));

/**
 * Fetch tenant context on app load.
 * Call this once from the root component or auth callback.
 */
export async function fetchTenantContext(): Promise<void> {
  return useTenantStore.getState().fetchTenantContext();
}

/**
 * Switch the active org for super-admin sessions.
 *
 * Updates the admin store's selectedOrgId (which causes all subsequent API
 * requests to include X-Tenant-Id), then re-fetches tenant context so the
 * displayed org name updates immediately.
 *
 * @param orgId - Target org_id, or null to revert to JWT/hostname resolution
 */
export async function switchOrg(orgId: number | null): Promise<void> {
  // Import lazily to avoid a circular dependency at module init time
  const { useAdminStore } = await import("./admin");
  useAdminStore.getState().setSelectedOrgId(orgId);
  await useTenantStore.getState().fetchTenantContext();
}
